/** @format */

/* eslint @typescript-eslint/no-unsafe-enum-comparison: "warn" */
import { TFunction } from 'i18next';

import { MarketSlug } from '@toucan/client/global/market';

import {
    ContentfulEntryType,
    HyperlinkEntry,
    TypenameTemplatePath,
    UrlSlugParamsType,
} from '@toucan/client/core/contentful';

import { ArticleHyperlinkEntry } from '../config/types';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ContentfulLinkDetails {
    url: string;
    templatePath: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface ContentfulLinkDetailEntry {
    __typename: ContentfulEntryType;
    urlSlug: string;
    categorySlug?: string;
}

/**
 * Returns link details based on a Contentful Link entry
 *
 * @param {ContentfulLinkDetailEntry} entry The entry to generate link details for
 * @param {string} marketSlug The market slug for the current site
 * @param {TFunction} t The i18next translation function
 * @return {ContentfulLinkDetails} Link details
 */
export function getContentfulLinkDetails(
    entry: ContentfulLinkDetailEntry,
    marketSlug: string,
    t: TFunction
): ContentfulLinkDetails {
    let linkDetails: ContentfulLinkDetails;
    const tOptions = {
        ns: 'urls',
    };

    switch (entry.__typename) {
        case ContentfulEntryType.CATEGORY:
            linkDetails = {
                url: `/${marketSlug}/${t('content', tOptions)}/${
                    entry.urlSlug
                }`,
                templatePath: `${TypenameTemplatePath.Category}?slug=${entry.urlSlug}`,
            };
            break;
        case ContentfulEntryType.ARTICLE:
            linkDetails = {
                url: `/${marketSlug}/${t('content', tOptions)}/${
                    entry.categorySlug
                }/${entry.urlSlug}`,
                templatePath: `${TypenameTemplatePath.Article}?articleSlug=${entry.urlSlug}&categorySlug=${entry.categorySlug}`,
            };
            break;
        case ContentfulEntryType.LANDING_PAGE:
            linkDetails = {
                url: `/${marketSlug}/${entry.urlSlug}`,
                templatePath: `${TypenameTemplatePath.LandingPage}?slug=${entry.urlSlug}`,
            };
            break;
        case ContentfulEntryType.BASIC_PAGE:
        default:
            linkDetails = {
                url: `/${marketSlug}/${entry.urlSlug}`,
                templatePath: TypenameTemplatePath.PageHelp,
            };
            break;
    }

    return linkDetails;
}

/**
 * Maps passed URL slugs to the correct market slug
 */
export function getUrlSlugs({
    urlSlug_GB,
    urlSlug_EU,
    urlSlug_US,
    urlSlug_DE,
    urlSlug_FR,
    urlSlug_NL,
    urlSlug_IT,
    urlSlug_ES,
    urlSlug_PT,
    urlSlug_EN_IE,
    urlSlug_DE_AT,
    urlSlug_FR_BE,
    urlSlug_NL_BE,
}: Record<string, any> & UrlSlugParamsType) {
    return {
        [MarketSlug.EN_UK]: urlSlug_GB ?? '',
        [MarketSlug.EN_EU]: urlSlug_EU ?? '',
        [MarketSlug.EN_US]: urlSlug_US ?? '',
        [MarketSlug.DE_DE]: urlSlug_DE ?? '',
        [MarketSlug.FR_FR]: urlSlug_FR ?? '',
        [MarketSlug.NL_NL]: urlSlug_NL ?? '',
        [MarketSlug.IT_IT]: urlSlug_IT ?? '',
        [MarketSlug.ES_ES]: urlSlug_ES ?? '',
        [MarketSlug.PT_PT]: urlSlug_PT ?? '',
        [MarketSlug.EN_IE]: urlSlug_EN_IE ?? '',
        [MarketSlug.DE_AT]: urlSlug_DE_AT ?? '',
        [MarketSlug.FR_BE]: urlSlug_FR_BE ?? '',
        [MarketSlug.NL_BE]: urlSlug_NL_BE ?? '',
    };
}

export function isArticleHyperlinkEntry(
    entry: HyperlinkEntry | ArticleHyperlinkEntry
): entry is ArticleHyperlinkEntry {
    return entry.__typename === ContentfulEntryType.ARTICLE;
}

export function getEntryLinkDetailsByTypename(
    entry: HyperlinkEntry | ArticleHyperlinkEntry
) {
    if (isArticleHyperlinkEntry(entry)) {
        return {
            __typename: entry.__typename,
            urlSlug: entry.urlSlug ?? '',
            categorySlug: entry.category?.urlSlug ?? '',
        };
    }

    return {
        __typename: entry.__typename,
        urlSlug: entry.urlSlug,
    };
}
